// onboardingFlow.ts — pure decisions for the signed-out → signed-up → first
// county path, split out of Dashboard.tsx so they are unit-testable without
// mounting the whole dashboard (same reason lib/countyOnboarding.ts exists).

import type { UserPlan } from "./planCapabilities";

// ─── Which AuthModal tab a deep link opens on ─────────────────────────────

// utm_source values our own alert/digest mail uses (or has used). Mail sent
// to existing accounts — the reader almost certainly has a login.
const ACCOUNT_EMAIL_SOURCES = new Set(["alert", "email_alert", "digest", "email_digest"]);

/**
 * The AuthModal tab a logged-out visitor should see when the hard wall
 * catches a `?property=` deep link.
 *
 * Alert and digest emails go only to people who already have an account, and
 * their links are a bare `/?property=<id>` or `/app?property=<id>` — so a bare
 * deep link defaults to "signin". Showing "Create account" there sent
 * returning users into a signup form for an email that already exists.
 *
 * Marketing entry points keep "signup": an explicit `auth=signup`, a link
 * shared from the property modal (tagged `ref=share`), or any campaign
 * `utm_source` that is not our own account mail (e.g. the newsletter, whose
 * list includes people who never made an account).
 */
export function deepLinkAuthMode(search: string): "signin" | "signup" {
  const params = new URLSearchParams(search);
  const auth = params.get("auth");
  if (auth === "signup") return "signup";
  if (auth === "login") return "signin";
  if (params.get("ref") === "share") return "signup";
  const source = (params.get("utm_source") || "").toLowerCase();
  const medium = (params.get("utm_medium") || "").toLowerCase();
  if (medium === "alert" || ACCOUNT_EMAIL_SOURCES.has(source)) return "signin";
  if (source) return "signup";
  return "signin";
}

// ─── County picker: skip = snooze, not "never ask again" ─────────────────

export const COUNTY_PICKER_SNOOZE_KEY = "parcely.countyPickerSnooze";
// "Next session", approximated: a skip silences the picker for the rest of
// today's visit (new tabs included) and it comes back on a later visit. The
// "Pick your free county" banner stays up meanwhile.
export const COUNTY_PICKER_SNOOZE_MS = 12 * 60 * 60 * 1000;

export function snoozeCountyPicker(userId: string, now: number = Date.now()): void {
  try {
    localStorage.setItem(COUNTY_PICKER_SNOOZE_KEY, JSON.stringify({ userId, until: now + COUNTY_PICKER_SNOOZE_MS }));
  } catch {
    /* storage blocked — the picker just re-prompts on the next load */
  }
}

export function isCountyPickerSnoozed(userId: string, now: number = Date.now()): boolean {
  try {
    const raw = localStorage.getItem(COUNTY_PICKER_SNOOZE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { userId?: unknown; until?: unknown };
    return parsed.userId === userId && typeof parsed.until === "number" && parsed.until > now;
  } catch {
    return false;
  }
}

export function clearCountyPickerSnooze(): void {
  try {
    localStorage.removeItem(COUNTY_PICKER_SNOOZE_KEY);
  } catch {
    /* ignore */
  }
}

function participatesInCountyPick(plan: UserPlan): boolean {
  return plan === "free" || plan === "scout";
}

/**
 * Auto-open the county picker for a free/scout user who has never SAVED a
 * county pick (countiesOnboardedAt is now stamped only on save), unless they
 * skipped it recently or already dismissed it in this page session.
 */
export function shouldAutoOpenCountyPicker(opts: {
  signedIn: boolean;
  planLoaded: boolean;
  profileLoaded: boolean;
  plan: UserPlan;
  countiesOnboardedAt: string | null;
  chosenCounties: string[];
  snoozed: boolean;
  dismissedThisSession: boolean;
}): boolean {
  if (!opts.signedIn || !opts.planLoaded || !opts.profileLoaded) return false;
  if (!participatesInCountyPick(opts.plan)) return false;
  // The onboarded stamp is deliberately NOT a reason to stay shut. The old
  // mount-time stamp marked users onboarded without a pick: on 2026-10-31, 18
  // of 41 free profiles with no county carried it, so they only ever saw the
  // dismissible banner and never an alert. No chosen county is the real
  // condition; the snooze still stops this from nagging.
  if (opts.chosenCounties.length > 0) return false;
  return !opts.snoozed && !opts.dismissedThisSession;
}

/** The persistent "Pick your free county" banner: any free/scout user with no
 *  chosen county, including users the old mount-time stamp marked onboarded
 *  without them ever picking. */
export function shouldShowPickCountyBanner(opts: {
  planLoaded: boolean;
  profileLoaded: boolean;
  plan: UserPlan;
  chosenCounties: string[];
  dismissed: boolean;
  pickerOpen: boolean;
}): boolean {
  if (!opts.planLoaded || !opts.profileLoaded || opts.dismissed || opts.pickerOpen) return false;
  return participatesInCountyPick(opts.plan) && opts.chosenCounties.length === 0;
}

// Banner dismissal is per tab session: it comes back on the next visit for as
// long as the user still has no county, which is the whole point of it.
export const PICK_COUNTY_BANNER_DISMISS_KEY = "parcely.pickCountyBannerDismissed";

export function isPickCountyBannerDismissed(userId: string): boolean {
  try {
    return sessionStorage.getItem(PICK_COUNTY_BANNER_DISMISS_KEY) === userId;
  } catch {
    return false;
  }
}

export function dismissPickCountyBanner(userId: string): void {
  try {
    sessionStorage.setItem(PICK_COUNTY_BANNER_DISMISS_KEY, userId);
  } catch {
    /* ignore */
  }
}
